/**
 * SecurityTab
 * ===========
 *
 * Account -> Security. Two-factor authentication (TOTP authenticator
 * app) for every user:
 *
 *   1. "Set up" mints a pending secret (POST /user/2fa/setup) and shows
 *      the QR code (otpauth:// URI rendered client-side, so the secret
 *      never leaves this page as an image) plus a manual-entry key.
 *   2. The first valid 6-digit code confirms the authenticator works
 *      (POST /user/2fa/enable) and returns ten single-use recovery
 *      codes — shown exactly once, with copy/download helpers.
 *   3. Once enabled: disable and recovery-code regeneration both
 *      require a current code, so a hijacked session can't quietly
 *      remove the second factor.
 *
 * If any of the user's organizations sets the ``two_factor_required``
 * policy and they haven't enrolled, a warning banner pushes them into
 * setup (login responses carry the same flag).
 *
 * Backend (via the ``security`` redux-saga namespace):
 *   GET  /user/2fa/status
 *   POST /user/2fa/setup
 *   POST /user/2fa/enable          { code }
 *   POST /user/2fa/disable         { code }
 *   POST /user/2fa/recovery-codes  { code }
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { QRCodeSVG } from 'qrcode.react';
import { useSnackbar } from 'notistack';

import { format } from '../../utils/datetime';
import {
  fetchRequest as statusRequest,
  clear as clearStatus,
} from '../../redux/actions/security/twoFactorStatus';
import {
  fetchRequest as setupRequest,
  clear as clearSetup,
} from '../../redux/actions/security/twoFactorSetup';
import {
  fetchRequest as enableRequest,
  clear as clearEnable,
} from '../../redux/actions/security/twoFactorEnable';
import {
  fetchRequest as disableRequest,
  clear as clearDisable,
} from '../../redux/actions/security/twoFactorDisable';
import {
  fetchRequest as regenRequest,
  clear as clearRegen,
} from '../../redux/actions/security/twoFactorRecoveryCodes';

// ---------------------------------------------------------------------------
// Recovery-codes block (shown once after enable / regenerate)
// ---------------------------------------------------------------------------

const RecoveryCodes = ({ codes }) => {
  const { enqueueSnackbar } = useSnackbar();

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      enqueueSnackbar('Recovery codes copied', { variant: 'success' });
    } catch (_e) {
      enqueueSnackbar('Could not copy — select and copy manually', { variant: 'warning' });
    }
  };

  const download = () => {
    const blob = new Blob(
      [`Predict.ai two-factor recovery codes\n\n${codes.join('\n')}\n\nEach code works once. Keep this file somewhere safe.\n`],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'predictai-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Save your recovery codes now
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Each code signs you in once if you lose your authenticator. They are
        shown only this one time.
      </Typography>
      <Box
        component="code"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(2, max-content)' },
          columnGap: 4,
          rowGap: 0.5,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'background.default',
          border: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 13,
          mb: 1.5,
        }}
      >
        {codes.map((c) => <span key={c}>{c}</span>)}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyAll}>
          Copy
        </Button>
        <Button size="small" startIcon={<DownloadIcon />} onClick={download}>
          Download
        </Button>
      </Stack>
    </Alert>
  );
};

// ---------------------------------------------------------------------------
// Code-confirmation dialog (disable / regenerate)
// ---------------------------------------------------------------------------

const CodeConfirmDialog = ({ open, title, body, confirmLabel, danger, onClose, onConfirm, busy }) => {
  const [code, setCode] = useState('');
  useEffect(() => { if (open) setCode(''); }, [open]);
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {body}
        </Typography>
        <TextField
          autoFocus fullWidth size="small" label="Authentication code"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && code.trim()) onConfirm(code.trim()); }}
          inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={danger ? 'error' : 'primary'}
          disabled={busy || !code.trim()}
          onClick={() => onConfirm(code.trim())}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

const SecurityTab = () => {
  const dispatch = useDispatch();

  const statusSlice = useSelector((s) => s.security_2fa_status) || {};
  const setupSlice = useSelector((s) => s.security_2fa_setup) || {};
  const enableSlice = useSelector((s) => s.security_2fa_enable) || {};
  const disableSlice = useSelector((s) => s.security_2fa_disable) || {};
  const regenSlice = useSelector((s) => s.security_2fa_recovery_codes) || {};

  const [initialized, setInitialized] = useState(false);
  const [enrollCode, setEnrollCode] = useState('');

  // Dialogs
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  // Fresh visit: drop any state a previous visit left behind (recovery
  // codes are shown exactly once), then load the status.
  useEffect(() => {
    dispatch(clearSetup());
    dispatch(clearEnable());
    dispatch(clearDisable());
    dispatch(clearRegen());
    dispatch(clearStatus());
    dispatch(statusRequest());
    setInitialized(true);
  }, [dispatch]);

  const status = statusSlice.response; // /2fa/status payload
  const loading = !initialized || (!statusSlice.response && !statusSlice.error);

  // Enrollment state — the pending secret lives in the setup slice.
  const setupInfo = setupSlice.response; // {secret, otpauth_uri}

  // Shown-once recovery codes (after enable or regenerate). The sagas
  // clear the sibling slice, so at most one of these is set.
  const recoveryCodes = enableSlice.response?.recovery_codes
    || regenSlice.response?.recovery_codes
    || null;

  // The status refresh after enable/disable/regenerate keeps busy=true
  // until the reload lands, like the old "await loadStatus()".
  const busy = Boolean(
    setupSlice.isLoading || enableSlice.isLoading || disableSlice.isLoading
    || regenSlice.isLoading || statusSlice.isLoading,
  );

  // Fresh enrollment secret -> reset the code field.
  useEffect(() => {
    if (setupSlice.response) setEnrollCode('');
  }, [setupSlice.response]);

  // Enable succeeded -> the enrollment form is gone; clear the field too.
  useEffect(() => {
    if (enableSlice.response) setEnrollCode('');
  }, [enableSlice.response]);

  // Close the dialogs when their operation succeeds.
  useEffect(() => {
    if (disableSlice.response) setDisableOpen(false);
  }, [disableSlice.response]);
  useEffect(() => {
    if (regenSlice.response) setRegenOpen(false);
  }, [regenSlice.response]);

  const startSetup = () => dispatch(setupRequest());

  const confirmEnroll = () => {
    if (!enrollCode.trim()) return;
    dispatch(enableRequest({ code: enrollCode.trim() }));
  };

  const handleDisable = (code) => dispatch(disableRequest({ code }));

  const handleRegenerate = (code) => dispatch(regenRequest({ code }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const enabled = Boolean(status?.enabled);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 1.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <GppGoodIcon fontSize="small" color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Security</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Protect your account with a second factor on top of your sign-in
        method.
      </Typography>

      {/* Org policy nudge */}
      {status?.org_requires_2fa && !enabled && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          Your organization requires two-factor authentication. Set it up
          below to stay compliant.
        </Alert>
      )}

      {/* Status row */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Two-factor authentication (authenticator app)
        </Typography>
        <Chip
          size="small"
          icon={enabled ? <GppGoodIcon /> : <GppBadIcon />}
          label={enabled ? 'Enabled' : 'Off'}
          color={enabled ? 'success' : 'default'}
          variant={enabled ? 'filled' : 'outlined'}
        />
      </Stack>

      {/* ── Enabled state ─────────────────────────────────────────────── */}
      {enabled && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Every sign-in asks for a 6-digit code from your authenticator app.
            {status?.confirmed_at && (
              <> Enabled {(() => { try { return format(new Date(status.confirmed_at), 'PP'); } catch (_e) { return ''; } })()}.</>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recovery codes remaining:{' '}
            <strong>{status?.recovery_codes_remaining ?? 0}</strong>
            {(status?.recovery_codes_remaining ?? 0) <= 2 && (
              <Typography component="span" variant="body2" color="warning.main">
                {' '}— generate a fresh set soon.
              </Typography>
            )}
          </Typography>

          {recoveryCodes && <RecoveryCodes codes={recoveryCodes} />}

          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => setRegenOpen(true)}>
              Regenerate recovery codes
            </Button>
            <Button variant="outlined" color="error" onClick={() => setDisableOpen(true)}>
              Disable 2FA
            </Button>
          </Stack>
        </>
      )}

      {/* ── Off, not mid-setup ────────────────────────────────────────── */}
      {!enabled && !setupInfo && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use any authenticator app (Google Authenticator, 1Password, Authy,
            Microsoft Authenticator, …). You'll scan a QR code, then every
            sign-in asks for a 6-digit code from the app.
          </Typography>
          {recoveryCodes && <RecoveryCodes codes={recoveryCodes} />}
          <Button
            variant="contained"
            startIcon={<QrCode2Icon />}
            onClick={startSetup}
            disabled={busy}
            sx={{ mt: recoveryCodes ? 2 : 0 }}
          >
            {busy ? 'Preparing…' : 'Set up two-factor authentication'}
          </Button>
        </>
      )}

      {/* ── Enrollment in progress ────────────────────────────────────── */}
      {!enabled && setupInfo && (
        <Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            1 · Scan this QR code with your authenticator app
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              bgcolor: '#fff',
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.divider}`,
              mb: 1.5,
            }}
          >
            <QRCodeSVG value={setupInfo.otpauth_uri} size={168} marginSize={0} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Can't scan? Enter this key manually:{' '}
            <Box component="code" sx={{ fontSize: 12, wordBreak: 'break-all' }}>
              {setupInfo.secret}
            </Box>
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            2 · Enter the 6-digit code the app shows
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <TextField
              size="small"
              label="Code"
              placeholder="123456"
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmEnroll(); }}
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
              sx={{ width: 160 }}
            />
            <Button variant="contained" onClick={confirmEnroll} disabled={busy || !enrollCode.trim()}>
              {busy ? 'Verifying…' : 'Verify & enable'}
            </Button>
            <Button color="inherit" onClick={() => dispatch(clearSetup())} disabled={busy}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      <CodeConfirmDialog
        open={disableOpen}
        title="Disable two-factor authentication?"
        body="Your account will no longer ask for an authenticator code at sign-in. Enter a current code (or a recovery code) to confirm."
        confirmLabel="Disable 2FA"
        danger
        busy={busy}
        onClose={() => setDisableOpen(false)}
        onConfirm={handleDisable}
      />
      <CodeConfirmDialog
        open={regenOpen}
        title="Regenerate recovery codes?"
        body="A new set of ten codes replaces the old ones — every previous recovery code stops working. Enter a current authenticator code to confirm."
        confirmLabel="Regenerate"
        busy={busy}
        onClose={() => setRegenOpen(false)}
        onConfirm={handleRegenerate}
      />
    </Paper>
  );
};

export default SecurityTab;
